// pages/chats.js — Chats page.
//
// Owns the `data-page="chats"` section. Aggregates the team's
// lease history into per-agent chat cards: which tool, which repo,
// what files, recency. Expandable card body with file-edit bars
// and recent-paths.
import { state } from "../state.js";
import { $, patchHTML } from "../dom.js";
import { api } from "../api.js";
import { escapeHTML, ago } from "../util.js";
import {
  parseAgentID,
  purposeToPath,
  inferChatLabel,
  worktreeFromPurpose,
  repoFor,
  toolIcon,
  chatDescription,
  shortPath,
} from "../format.js";
import { deviceLabelFor, myMemberName } from "../members.js";
import { aggregateChats, chatStatus, resolveChatMember } from "../chats.js";
import { isCloudMode } from "../ui.js";
import { CHATS_PAGE_SIZE } from "../constants.js";

// chatScope returns the resolved scope: "mine" only when, in cloud mode,
// the user has explicitly flipped the toggle to Mine; otherwise "team".
//
// Default is "team" so EVERY member keeps today's whole-team Chats view
// unchanged — "Mine" is an opt-in narrowing (like the Leases page's
// "mine only"), not a new default. Unlike the Overview's team toggle,
// this isn't admin-gated: the Chats list is already team-visible to all
// members, so letting anyone filter it to themselves exposes nothing
// new. The toggle stays .cloud-only (no team concept in local-daemon).
function chatScope() {
  if (isCloudMode() && state.chatScopeView === "mine") {
    return "mine";
  }
  return "team";
}

// One-time fetch of /v1/config to learn track_reads, so the "Browsing —
// reads aren't tracked" hint can render on chat cards. The Settings page sets
// state.trackReads when opened, but a user who lands on Chats first would
// otherwise see state.trackReads === null forever (no hint). Fetch once in the
// background and re-render when it lands. Mirrors overview.js's fetch-once
// pattern. Guard prevents a fetch storm across the render loop's many ticks.
//
// LOCAL-DAEMON ONLY. /v1/config is a daemon endpoint; on the hosted dashboard
// api() routes to the coordinator worker, which has no /v1/config route, so
// fetching there 404s. track_reads is a per-machine daemon setting that the
// coordinator can't know anyway, so the "reads aren't tracked" hint is a
// local-daemon concept — skip the fetch (and the hint) in cloud mode, leaving
// state.trackReads null so no hint shows. (The Settings page already gates its
// own /v1/config fetch behind local-daemon mode for the same reason.)
let trackReadsFetched = false;
function ensureTrackReadsKnown() {
  if (trackReadsFetched || state.trackReads !== null) return;
  if (isCloudMode()) return; // no /v1/config on the coordinator
  trackReadsFetched = true;
  api("/v1/config")
    .then((data) => {
      if (data && typeof data.track_reads === "boolean") {
        state.trackReads = data.track_reads;
        renderChatsPage(); // repaint so the hint reflects the now-known value
      }
    })
    .catch(() => {
      // Couldn't read config (e.g. permission / offline). Leave trackReads
      // null so no misleading hint shows; don't retry-storm.
    });
}

export function renderChatsPage() {
  ensureTrackReadsKnown();
  let chats = aggregateChats();
  // Keep the scope toggle + member chips in sync (cloud-only, idempotent
  // — safe to call every render). Pass the aggregated chats so the chip
  // builder doesn't re-aggregate.
  bindChatScopeControl();
  renderChatMemberChips(chats);
  const scope = chatScope();
  // Mine scope: narrow to the signed-in user's own chats by comparing
  // each chat's resolved member to mine. If we can't resolve our own
  // member yet, don't filter — the team-wide list is a safer default
  // than an empty page.
  if (scope === "mine") {
    const mine = myMemberName();
    if (mine) {
      chats = chats.filter((c) => resolveChatMember(c.agentID) === mine);
    }
  } else if (state.chatMemberFilter) {
    // Team scope + a member chip selected: narrow to that member. If
    // that member no longer has any active chat (aged out), clear the
    // stale filter so the user sees the full team list, not an empty
    // page with no active chip.
    const stillPresent = chats.some(
      (c) => resolveChatMember(c.agentID) === state.chatMemberFilter,
    );
    if (stillPresent) {
      chats = chats.filter((c) => resolveChatMember(c.agentID) === state.chatMemberFilter);
    } else {
      state.chatMemberFilter = "";
    }
  }
  const chatFilter = (state.chatFilterText || "").toLowerCase();
  if (chatFilter) {
    chats = chats.filter((c) => {
      const agent = parseAgentID(c.agentID);
      const recentPath = c.leases[0]
        ? purposeToPath(c.leases[0].Purpose)
        : "";
      const label = inferChatLabel(c.agentID, recentPath, c);
      const wt = c.leases[0]
        ? worktreeFromPurpose(c.leases[0].Purpose)
        : "";
      const repo = c.leases[0]
        ? repoFor(c.leases[0].Resource, c.leases[0].Purpose, c.leases[0].Repo)
        : "";
      const fields = [
        c.agentID,
        agent.tool,
        agent.shortID,
        label.name,
        label.detail,
        wt,
        repo,
      ];
      return fields.some((f) =>
        (f || "").toLowerCase().includes(chatFilter),
      );
    });
  }
  const list = $("chats-list");
  if (!list) return;
  // Loading vs empty are DIFFERENT states and must not render the same.
  //
  // Before this (2026-08-28) the boot fetch's ~1s window rendered the
  // "No active chats in the last hour" panel — a confident, wrong claim
  // complete with troubleshooting advice telling the user to run `agc heal`
  // on a perfectly healthy daemon. state.loading is already a one-shot
  // "boot fetch has completed at least once" flag owned by bootstrap.js;
  // it just was never read here. The skeleton classes and the shimmer
  // keyframe already existed in dashboard.css too — the page simply never
  // used either.
  if (state.loading && chats.length === 0) {
    patchHTML(
      list,
      `<div class="panel" aria-busy="true" aria-label="Loading chats">
        ${[0, 1, 2]
          .map(
            () => `<div class="chat-skel">
            <div class="skeleton skeleton-text med"></div>
            <div class="skeleton skeleton-text short"></div>
          </div>`,
          )
          .join("")}
      </div>`,
    );
    return;
  }
  if (chats.length === 0) {
    patchHTML(
      list,
      `<div class="panel"><div class="empty">
        <div class="empty-title">No active chats in the last hour</div>
        <div class="empty-detail">A chat appears here as soon as a team member's AI session runs a tool call — Read, Bash, Edit, Write, anything. If you're chatting now and don't see your session, your daemon may be a few versions behind: run <code>agc heal</code> to restart it. <a href="https://docs.agentcollision.com/quickstart.html" style="font-weight: 600;">Read the quickstart &rarr;</a></div>
      </div></div>`,
    );
    return;
  }
  const totalPages = Math.max(
    1,
    Math.ceil(chats.length / CHATS_PAGE_SIZE),
  );
  if (!state.chatPage) state.chatPage = 0;
  if (state.chatPage >= totalPages) state.chatPage = totalPages - 1;
  const start = state.chatPage * CHATS_PAGE_SIZE;
  const pageChats = chats.slice(start, start + CHATS_PAGE_SIZE);
  let html = pageChats.map(renderChatCard).join("");
  if (chats.length > CHATS_PAGE_SIZE) {
    const prevD = state.chatPage === 0 ? "disabled" : "";
    const nextD = state.chatPage >= totalPages - 1 ? "disabled" : "";
    html += `<div class="pagination" style="border-radius: var(--r-lg); margin-top: var(--s3);">
      <div class="info">Showing <strong style="color: var(--text);">${start + 1}&ndash;${start + pageChats.length}</strong> of <strong style="color: var(--text);">${chats.length}</strong> chats</div>
      <div class="controls">
        <button data-chat-page="prev" ${prevD}>&lsaquo; Prev</button>
        <span class="page-indicator">Page ${state.chatPage + 1} / ${totalPages}</span>
        <button data-chat-page="next" ${nextD}>Next &rsaquo;</button>
      </div>
    </div>`;
  }
  patchHTML(list, html);
}

// bindChatScopeControl wires the Mine/Team toggle. Gating: .cloud-only
// (hidden in local-daemon) but available to ALL members — unlike the
// Overview's admin-gated team toggle, the Chats list is already
// team-visible, so a self-filter is just a focus tool. Idempotent +
// re-callable — the click listener attaches once (dataset guard); every
// call refreshes visibility + active state. Switching to "mine" clears
// any selected member chip.
function bindChatScopeControl() {
  const host = document.getElementById("chats-scope-control");
  if (!host) return;
  // Cloud-only (no team concept in local-daemon), but available to ALL
  // members — the Chats list is already team-visible, so a self-filter
  // exposes nothing new. No admin gate.
  if (!isCloudMode()) {
    host.style.display = "none";
    return;
  }
  host.style.display = "";
  const current = chatScope();
  host.querySelectorAll(".segmented-button").forEach((btn) => {
    btn.classList.toggle("active", btn.getAttribute("data-chat-scope") === current);
  });
  if (host.dataset.scopeWired === "1") return;
  host.dataset.scopeWired = "1";
  host.querySelectorAll(".segmented-button").forEach((btn) => {
    btn.addEventListener("click", () => {
      const v = btn.getAttribute("data-chat-scope");
      if (!v || v === chatScope()) return;
      state.chatScopeView = v;
      if (v === "mine") state.chatMemberFilter = "";
      state.chatPage = 0;
      host.querySelectorAll(".segmented-button").forEach((b) => {
        b.classList.toggle("active", b === btn);
      });
      renderChatsPage();
    });
  });
}

// renderChatMemberChips draws one chip per team member who has an active
// chat, in Team view only. Clicking a chip narrows the list to that
// member (state.chatMemberFilter); an "all" chip clears it. Mirrors the
// .filter-chip pattern from the Leases page. Hidden in Mine view and in
// local-daemon (no team concept).
function renderChatMemberChips(chats) {
  const host = document.getElementById("chats-member-chips");
  if (!host) return;
  if (chatScope() !== "team") {
    host.style.display = "none";
    host.innerHTML = "";
    return;
  }
  // Distinct resolved members across the current (team-wide) chat list.
  // Uses the already-aggregated chats passed by renderChatsPage; falls
  // back to aggregating if called without an argument.
  const members = new Set();
  for (const c of chats || aggregateChats()) {
    const m = resolveChatMember(c.agentID);
    if (m) members.add(m);
  }
  if (members.size === 0) {
    host.style.display = "none";
    host.innerHTML = "";
    return;
  }
  host.style.display = "flex";
  const sel = state.chatMemberFilter || "";
  const allActive = sel === "" ? "active" : "";
  let html = `<button class="filter-chip ${allActive}" data-chat-member="" data-tooltip="Show every team member's chats">all</button>`;
  for (const m of Array.from(members).sort()) {
    const active = sel === m ? "active" : "";
    html += `<button class="filter-chip ${active}" data-chat-member="${escapeHTML(m)}" data-tooltip="${escapeHTML(m)}">${escapeHTML(m)}</button>`;
  }
  patchHTML(host, html);
}

function renderChatCard(c) {
  const recentLeases = c.leases
    .slice()
    .sort((a, b) => new Date(b.AcquiredAt) - new Date(a.AcquiredAt))
    .slice(0, 8);
  const recentPath = recentLeases[0]
    ? purposeToPath(recentLeases[0].Purpose)
    : "";
  const label = inferChatLabel(c.agentID, recentPath, c);
  const agent = parseAgentID(c.agentID);
  // Lead the card with WHAT the chat is doing (the inferred activity label) —
  // that's what distinguishes one chat from another. When all chats belong to
  // the same person (the common solo case), a member-name title made every card
  // read identically ("vincent", "vincent", …); the activity is the useful
  // signal. The member is shown as a small secondary chip in Team view so you
  // still see WHO without it swallowing the title.
  const teamMember = chatScope() === "team" ? resolveChatMember(c.agentID) : "";
  // When the activity is the generic "Browsing" AND we have no member to chip,
  // append a short id so multiple browsing chats stay distinguishable rather
  // than all reading "Browsing". The repo/description lines differentiate too,
  // but the title shouldn't be identical across cards.
  const displayName =
    label.name === "Browsing" && !teamMember
      ? "Browsing · " + agent.shortID.slice(0, 6)
      : label.name;
  const wt = recentLeases[0]
    ? worktreeFromPurpose(recentLeases[0].Purpose)
    : "";
  // Pick the repo that appears most often across this chat's recent
  // leases. A chat usually works in one repo at a time, but a chat
  // bouncing between repos gets the dominant one rather than the
  // most recent one — better signal of "what is this chat doing".
  const repoCounts = new Map();
  for (const l of recentLeases) {
    const r = repoFor(l.Resource, l.Purpose, l.Repo);
    if (!r) continue;
    repoCounts.set(r, (repoCounts.get(r) || 0) + 1);
  }
  let repo = "";
  let repoMax = 0;
  for (const [r, n] of repoCounts) {
    if (n > repoMax) {
      repo = r;
      repoMax = n;
    }
  }

  const fileEntries = Array.from(c.files.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  const subTotal = fileEntries.reduce((s, [, n]) => s + n, 0);

  const status = chatStatus(c);
  const dotClass =
    status === "active"
      ? ""
      : status === "browsing"
        ? "browsing"
        : status === "idle"
          ? "idle"
          : "gone";
  const statusLabel =
    status === "active"
      ? "Editing"
      : status === "browsing"
        ? "Working"
        : status === "idle"
          ? "Idle"
          : "Inactive";
  const statusTip =
    status === "active"
      ? "This chat holds files it is editing right now."
      : status === "browsing"
        ? "This chat is working right now (reading, running commands or thinking) but isn’t editing a file."
        : status === "idle"
          ? "This chat was active in the last 10 minutes and is quiet right now."
          : "This chat hasn’t been heard from in over 10 minutes. Its leases are eligible for reclamation.";
  const cardId = "chat-" + agent.shortID;
  const isExpanded =
    state.expandedChats && state.expandedChats.has(c.agentID);

  return `
    <div class="chat-card" id="${cardId}">
      <div class="chat-card-header" style="cursor: pointer;" data-chat-toggle="${escapeHTML(c.agentID)}">
        <div class="chat-card-id">
          <span class="chat-status-dot ${dotClass}"></span>
          <span style="font-size: 18px;">${toolIcon(agent.tool)}</span>
          <div class="chat-label">
            <span class="name" style="display: flex; align-items: center; gap: var(--s2);">
              <span class="chat-title-text">${escapeHTML(displayName)}</span>
              ${teamMember ? `<span class="chat-member-chip" data-tooltip="Run by ${escapeHTML(teamMember)}">${escapeHTML(teamMember)}</span>` : ""}
              <span class="state-badge state-${status === "active" ? "held" : status === "browsing" ? "expired" : status === "idle" ? "expired" : "released"}" style="font-size: 9px; padding: 1px 6px;" data-tooltip="${statusTip}">${statusLabel}</span>
            </span>
            <span style="font-size: 11px; color: var(--text-tertiary); line-height: 1.4; margin-top: 2px;">${escapeHTML(chatDescription(c, status, label, agent))}</span>
            <span style="font-size: 10px; color: var(--text-tertiary); opacity: 0.7; margin-top: 1px; font-family: var(--font-mono);">${escapeHTML(agent.tool)} · ${escapeHTML(deviceLabelFor(c) || "?")}${repo ? ` · <span style="color: var(--info); font-weight: 600;" data-tooltip="Repo this chat is editing in">${escapeHTML(repo)}</span>` : ""}</span>
          </div>
        </div>
        <div class="chat-card-meta">
          <div class="recent">${escapeHTML(ago(new Date(c.mostRecentTs).toISOString()))}</div>
          <div>${(() => {
            // Prefer the durable writes-table count (survives lease expiry)
            // over the transient lease count. Falls back to lease count for
            // pre-056 coordinators / older daemons that don't send edit_count.
            const edits = Math.max(c.totalLeases || 0, c.editCount || 0);
            return c.browsing && edits === 0
              ? "reading" + (c.lastTool ? " via " + escapeHTML(c.lastTool) : "")
              : edits + " edit" + (edits === 1 ? "" : "s") + " · " + c.held + " writing now";
          })()}</div>
          <div style="color: var(--text-tertiary); font-size: 11px; margin-top: 2px;">${isExpanded ? "&#9660; collapse" : "&#9654; expand"}</div>
        </div>
      </div>

      <div style="display: ${isExpanded ? "block" : "none"};">
        ${
          fileEntries.length === 0
            ? ""
            : `
        <div class="chat-card-section">
          <div class="chat-card-section-label" data-tooltip="File edits in the last hour, grouped by which part of the codebase was touched.">
            Editing in <span class="help-icon">?</span>
          </div>
          ${fileEntries
            .map(([name, count]) => {
              const pct = Math.round((count / subTotal) * 100);
              return `
              <div class="file-bar">
                <div class="file-bar-label">
                  <span class="name">${escapeHTML(name)}</span>
                  <span class="pct">${count} edit${count === 1 ? "" : "s"} &middot; ${pct}%</span>
                </div>
                <div class="file-bar-track"><div class="file-bar-fill" style="width: ${pct}%;"></div></div>
              </div>`;
            })
            .join("")}
        </div>`
        }

        <div class="chat-card-section">
          <div class="chat-card-section-label" data-tooltip="Actual file paths the chat acquired leases on, most recent first.">
            Recent edits <span class="help-icon">?</span>
          </div>
          <div class="recent-files-list">
            ${recentLeases
              .map((l) => {
                const path = purposeToPath(l.Purpose);
                return `
                <div class="recent-file-row">
                  <span class="path">${escapeHTML(shortPath(path) || l.Resource)}</span>
                  <span class="age">${escapeHTML(ago(l.AcquiredAt))}</span>
                </div>`;
              })
              .join("")}
          </div>
        </div>

        <div class="chat-card-section">
          <div class="chat-card-section-label" data-tooltip="Last file this chat read or browsed. Updates on every Read, Grep, or Glob tool call.">
            Last read <span class="help-icon">?</span>
          </div>
          <div class="recent-files-list">
            ${
              c.lastFile
                ? `
              <div class="recent-file-row">
                <span class="path">${escapeHTML(shortPath(c.lastFile) || c.lastFile)}</span>
                <span class="age">via ${escapeHTML(c.lastTool || "Read")}</span>
              </div>`
                : `<div style="color: var(--text-tertiary); font-size: 12px;">No reads recorded yet</div>`
            }
          </div>
        </div>
      </div>
    </div>`;
}

// ---- mount ----

let started = false;
let tickHandler = null;

function start() {
  if (started) return;
  started = true;
  renderChatsPage();
  tickHandler = () => {
    if (state.activePage === "chats") renderChatsPage();
  };
  document.addEventListener("dashboard:tick", tickHandler);
}

function stop() {
  if (!started) return;
  started = false;
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="chats"]');
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}
